import deburr from "lodash/deburr";
import { normalizeDecimalSeparator } from "utils/formatters";
import { formatDate } from "utils/helper";
import {
  DELIVERY_NOTE_COLUMN_TYPES,
  DELIVERY_NOTE_CURRENCY_CODES,
  DELIVERY_NOTE_IMAGE_MAX_SIZE,
  DELIVERY_NOTE_PDF_NAME_MODES,
} from "constants/constants";
import {
  DELIVERY_NOTE_DEFAULT_SIGNATORIES,
  DELIVERY_NOTE_FORM_DEFAULT_VALUES,
} from "constants/forms";

export const hasCustomPdfFileName = (template) =>
  !!template?.pdf_file_name?.trim();

const getPdfFileNameSuffix = (date = new Date()) => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);
  return `_${month}${year}`;
};

export const buildPdfFileNameExample = ({
  fileName,
  withSuffix,
  date = new Date(),
}) => {
  const trimmed = fileName?.trim();
  if (!trimmed) return "";
  return `${trimmed}${withSuffix ? getPdfFileNameSuffix(date) : ""}.pdf`;
};

export const getOfferedCurrencies = (currencyList) => {
  const offered = (currencyList || []).filter((elm) =>
    DELIVERY_NOTE_CURRENCY_CODES.includes(elm.currency_id)
  );
  return offered.length ? offered : currencyList || [];
};

export const getCurrencySymbol = ({ currencyList, currencyId }) =>
  currencyList?.find((elm) => elm.id === currencyId)?.currency_symbol || "";

export const hasLinkedColumn = (columns) =>
  !!columns?.some(
    (column) => column.type === DELIVERY_NOTE_COLUMN_TYPES.LINKED
  );

export const countColumnsByType = (columns) => ({
  simple:
    columns?.filter(
      (column) => column.type === DELIVERY_NOTE_COLUMN_TYPES.SIMPLE
    ).length || 0,
  linked:
    columns?.filter(
      (column) => column.type === DELIVERY_NOTE_COLUMN_TYPES.LINKED
    ).length || 0,
});

export const sortTemplatesByFavorite = (templates) =>
  [...(templates || [])].sort(
    (a, b) => Number(!!b.is_favorite) - Number(!!a.is_favorite)
  );

const normalizeText = (value) =>
  String(value ?? "")
    .trim()
    .replaceAll(/\s+/g, " ")
    .toLowerCase();

const normalizeSearchText = (value) => deburr(normalizeText(value));

const matchesTokens = (values, tokens) => {
  const haystack = values.map(normalizeSearchText).filter(Boolean).join(" ");
  return tokens.every((token) => haystack.includes(token));
};

const matchesSelectedParty = (party, selected) => {
  if (!selected?.length) return true;
  if (!party) return false;
  return selected.some((option) =>
    party.company_id != null && option?.value != null
      ? String(option.value) === String(party.company_id)
      : !!party.company_name &&
        normalizeSearchText(option?.label) ===
          normalizeSearchText(party.company_name)
  );
};

const searchTokens = (name) =>
  normalizeSearchText(name).split(" ").filter(Boolean);

const filterBySearch = (items, filters, getSearchValues) => {
  const { name = "", branches = [], clients = [] } = filters || {};
  const tokens = searchTokens(name);
  return (items || []).filter(
    (item) =>
      (!tokens.length || matchesTokens(getSearchValues(item), tokens)) &&
      matchesSelectedParty(item?.issuer, branches) &&
      matchesSelectedParty(item?.recipient, clients)
  );
};

export const filterTemplates = (templates, filters) =>
  filterBySearch(templates, filters, (template) => [template?.name]);

export const filterDeliveryNotes = (notes, filters) =>
  filterBySearch(notes, filters, (note) => [
    note?.template_name,
    note?.order_number,
  ]);

export const hasActiveDeliveryNoteFilters = (filters) => {
  const { name = "", branches = [], clients = [] } = filters || {};
  return !!normalizeSearchText(name) || !!branches.length || !!clients.length;
};

export const isTemplateNameTaken = ({ name, templates, excludedId }) => {
  const normalized = normalizeText(name);
  return !!templates?.some(
    (template) =>
      (excludedId == null || String(template?.id) !== String(excludedId)) &&
      normalizeText(template?.name) === normalized
  );
};

const buildPartyValues = (prefix, party) => ({
  [`${prefix}_logo_file_id`]: party?.logo_file_id || null,
  [`${prefix}_company`]: party?.company_id
    ? [{ value: party.company_id, label: party.company_name }]
    : [],
  [`${prefix}_address`]: party?.address || "",
  [`${prefix}_siret`]: party?.siret || "",
  [`${prefix}_phone`]: party?.phone || "",
});

export const buildFormValuesFromTemplate = (template) => {
  if (!template) return DELIVERY_NOTE_FORM_DEFAULT_VALUES;
  return {
    ...DELIVERY_NOTE_FORM_DEFAULT_VALUES,
    name: template.name || "",
    title: template.title || "",
    subtitle: template.subtitle || "",
    currency_id: template.currency_id || "",
    pdf_file_name: template.pdf_file_name || "",
    pdf_file_name_mode: hasCustomPdfFileName(template)
      ? DELIVERY_NOTE_PDF_NAME_MODES.CUSTOM
      : DELIVERY_NOTE_PDF_NAME_MODES.AUTOMATIC,
    pdf_suffix_enabled: !!template.pdf_suffix_enabled,
    ...buildPartyValues("issuer", template.issuer),
    ...buildPartyValues("recipient", template.recipient),
    columns: template.columns || [],
    issuer_signature_file_id: template.issuer_signature_file_id || null,
    issuer_signatory_name: template.issuer_signatory_name || "",
    recipient_signature_file_id: template.recipient_signature_file_id || null,
    recipient_signatory_name: template.recipient_signatory_name || "",
  };
};

export const resizeImageToBlob = ({
  file,
  maxSize = DELIVERY_NOTE_IMAGE_MAX_SIZE,
}) =>
  new Promise((resolve, reject) => {
    if (!file) {
      resolve(null);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Error decoding image"));
    };
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      try {
        const largestSide = Math.max(image.width, image.height);
        if (!largestSide) {
          reject(new Error("Image has no intrinsic dimensions"));
          return;
        }
        const ratio = Math.min(1, maxSize / largestSide);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(image.width * ratio);
        canvas.height = Math.round(image.height * ratio);
        const context = canvas.getContext("2d");
        if (!context) {
          reject(new Error("Canvas 2d context unavailable"));
          return;
        }
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const isPng = file.type === "image/png";
        canvas.toBlob(
          (blob) =>
            blob ? resolve(blob) : reject(new Error("Image encoding failed")),
          isPng ? "image/png" : "image/jpeg",
          isPng ? undefined : 0.85
        );
      } catch (e) {
        reject(e);
      }
    };
    image.src = objectUrl;
  });

export const formatCompanyAddress = ({ address, postalCode, city }) =>
  [address, [postalCode, city].filter(Boolean).join(" ").trim()]
    .filter(Boolean)
    .join("\n");

export const getSignatoryName = ({ source, party, intl }) =>
  source[`${party}_signatory_name`]?.trim() ||
  intl.formatMessage(DELIVERY_NOTE_DEFAULT_SIGNATORIES[party]);

const buildPartySnapshot = ({
  prefix,
  logoFileId,
  name,
  address,
  siret,
  signatureFileId,
  signatoryName,
}) => ({
  [`${prefix}_logo_file_id`]: logoFileId,
  [`${prefix}_name`]: name,
  [`${prefix}_address`]: address,
  [`${prefix}_siret`]: siret,
  [`${prefix}_signature_file_id`]: signatureFileId,
  [`${prefix}_signatory_name`]: signatoryName,
});

export const buildSnapshotFromFormValues = ({ values, intl }) => {
  const party = (prefix) =>
    buildPartySnapshot({
      prefix,
      logoFileId: values[`${prefix}_logo_file_id`],
      name: values[`${prefix}_company`]?.[0]?.label,
      address: values[`${prefix}_address`],
      siret: values[`${prefix}_siret`],
      signatureFileId: values[`${prefix}_signature_file_id`],
      signatoryName: getSignatoryName({ source: values, party: prefix, intl }),
    });

  return {
    title: values.title,
    subtitle: values.subtitle,
    ...party("issuer"),
    ...party("recipient"),
    columns: values.columns,
  };
};

export const buildSnapshotFromTemplate = ({ template, intl }) => {
  if (!template) return null;
  const party = (prefix) =>
    buildPartySnapshot({
      prefix,
      logoFileId: template[prefix]?.logo_file_id || null,
      name: template[prefix]?.company_name,
      address: template[prefix]?.address,
      siret: template[prefix]?.siret,
      signatureFileId: template[`${prefix}_signature_file_id`] || null,
      signatoryName: getSignatoryName({
        source: template,
        party: prefix,
        intl,
      }),
    });

  return {
    title: template.title,
    subtitle: template.subtitle,
    ...party("issuer"),
    ...party("recipient"),
    columns: template.columns,
  };
};

export const buildDeliveryNoteLineHeaders = (columns) =>
  (columns || []).flatMap((column, position) =>
    column.type === DELIVERY_NOTE_COLUMN_TYPES.LINKED
      ? [
          { position, field: "a", label: column.name_a },
          { position, field: "b", label: column.name_b },
          { position, field: "total" },
        ]
      : [{ position, field: "value", label: column.name }]
  );

const parseAmount = (input) => {
  if (typeof input === "number") return Number.isFinite(input) ? input : null;
  const normalized = normalizeDecimalSeparator(String(input ?? "").trim());
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

export const readLineCell = (line, position) => {
  if (line?.cells) return line.cells[position] || {};
  const cell = line?.values?.find((item) => item.column_position === position);
  return cell ? { a: cell.value_a, b: cell.value_b, value: cell.value } : {};
};

const isCellFilled = ({ column, cell }) =>
  column?.type === DELIVERY_NOTE_COLUMN_TYPES.LINKED
    ? !!String(cell?.a ?? "").trim() || !!String(cell?.b ?? "").trim()
    : !!String(cell?.value ?? "").trim();

const isDeliveryNoteLineFilled = ({ line, columns }) =>
  (columns || []).some((column, position) =>
    isCellFilled({ column, cell: readLineCell(line, position) })
  );

const filterFilledDeliveryNoteLines = ({ lines, columns }) =>
  (lines || []).filter((line) => isDeliveryNoteLineFilled({ line, columns }));

export const computeLineAmount = ({ column, cell }) => {
  if (column?.type !== DELIVERY_NOTE_COLUMN_TYPES.LINKED) return null;
  const a = parseAmount(cell?.a);
  const b = parseAmount(cell?.b);
  return a === null || b === null ? null : a * b;
};

const computeLineTotal = ({ columns, line }) =>
  (columns || []).reduce((total, column, position) => {
    const amount = computeLineAmount({
      column,
      cell: readLineCell(line, position),
    });
    if (amount === null) return total;
    return (total ?? 0) + amount;
  }, null);

export const formatDeliveryNoteAmount = ({ intl, amount, currencySymbol }) => {
  if (amount === null || amount === undefined) return "—";
  const value = intl.formatNumber(amount, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currencySymbol ? `${value} ${currencySymbol}` : value;
};

export const computeDeliveryNoteTotal = ({ columns, lines }) =>
  (lines || []).reduce(
    (total, line) => total + (computeLineTotal({ columns, line }) ?? 0),
    0
  );

export const paginateDeliveryNoteLines = ({
  rowHeights,
  headFirstHeight,
  headNextHeight,
  tableHeaderHeight,
  footerHeight,
  availableHeight,
}) => {
  const pages = [];
  let current = [];
  let used = headFirstHeight + tableHeaderHeight;

  rowHeights.forEach((height, index) => {
    if (current.length && used + height > availableHeight) {
      pages.push(current);
      current = [];
      used = headNextHeight + tableHeaderHeight;
    }
    current.push(index);
    used += height;
  });
  pages.push(current);

  if (used + footerHeight <= availableHeight) return pages;

  const lastPage = pages.at(-1);
  const footerPage = [];
  let footerPageUsed = headNextHeight + tableHeaderHeight + footerHeight;
  while (lastPage.length > 1 && used + footerHeight > availableHeight) {
    const index = lastPage.at(-1);
    if (footerPageUsed + rowHeights[index] > availableHeight) break;
    lastPage.pop();
    footerPage.unshift(index);
    used -= rowHeights[index];
    footerPageUsed += rowHeights[index];
  }
  pages.push(footerPage);
  return pages;
};

export const buildDeliveryNoteContentPayload = ({ draft }) => {
  const columns = draft?.template?.columns || [];
  return {
    order_number: draft?.orderNumber?.trim() || null,
    delivery_date: draft?.deliveryDate || null,
    lines: filterFilledDeliveryNoteLines({
      lines: draft?.lines,
      columns,
    }).map((line) => ({
        values: columns.map((column, position) => {
          const cell = readLineCell(line, position);
          return column.type === DELIVERY_NOTE_COLUMN_TYPES.LINKED
            ? {
                column_position: position,
                value: null,
                value_a: parseAmount(cell.a),
                value_b: parseAmount(cell.b),
              }
            : {
                column_position: position,
                value: cell.value?.trim() || null,
                value_a: null,
                value_b: null,
              };
        }),
      })),
  };
};

export const buildDeliveryNotePayload = ({ template, draft }) => ({
  template_id: template?.id,
  ...buildDeliveryNoteContentPayload({ draft }),
});

export const buildDeliveryNoteCopyPayload = ({ note, draft }) => ({
  version_id: note?.version_id,
  ...buildDeliveryNoteContentPayload({ draft }),
});

const buildTemplateFromNote = (note) => ({
  ...note,
  id: null,
  name: note?.template_name,
  columns: note?.columns || [],
});

export const buildDraftFromNote = (note) => ({
  template: buildTemplateFromNote(note),
  number: note?.number || "",
  orderNumber: note?.order_number || "",
  deliveryDate: note?.delivery_date || "",
  lines: (note?.lines || []).map((line, index) => ({
    key: `note-line-${index}`,
    cells: Object.fromEntries(
      (line?.values || []).map((value) => [
        value.column_position,
        {
          value: value.value ?? "",
          a: value.value_a ?? "",
          b: value.value_b ?? "",
        },
      ])
    ),
  })),
});

export const buildDeliveryNoteRenderNote = ({
  note,
  snapshot,
  currencySymbol,
}) => ({
  ...note,
  currencySymbol,
  total: snapshot
    ? computeDeliveryNoteTotal({
        columns: snapshot.columns,
        lines: note?.lines,
      })
    : null,
});

export const buildDeliveryNoteDocumentData = ({ draft, lang }) => ({
  number: draft.number,
  orderNumber: draft.orderNumber,
  deliveryDate: draft.deliveryDate ? formatDate(draft.deliveryDate, lang) : "",
  lines: draft.lines,
});

const sanitizeFileName = (name) => name.replaceAll(/[\\/:*?"<>|]/g, "-").trim();

export const buildDeliveryNotePdfFileName = ({
  template,
  number,
  date = new Date(),
}) => {
  if (hasCustomPdfFileName(template)) {
    const configured = template.pdf_file_name.trim();
    return sanitizeFileName(
      `${configured}${template?.pdf_suffix_enabled ? getPdfFileNameSuffix(date) : ""}`
    );
  }
  return sanitizeFileName(number || "");
};
